import {
  Component,
  ComponentInterface,
  Host,
  Prop,
  Element,
  h,
  Event,
  EventEmitter,
  State,
  Method,
  Watch
} from "@stencil/core"

import {
  addDays,
  startOfWeek,
  endOfWeek,
  setMonth,
  setYear,
  clamp,
  inRange,
  endOfMonth,
  startOfMonth,
  printISODate,
  parseISODate,
  DaysOfWeek,
} from "../../utils/date-utils";

import { StzhCalendarChangeEvent, StzhDropdownChangeEvent } from "../../index";
import { StzhCalendarDateDisabledPredicate } from "../../index";

import { StzhCalendarMonth } from "./stzh-calendar-month";
import { StzhCalendarLocalizedText } from "./stzh-calendar.localization";

function range(from: number, to: number) {
  var result: number[] = []
  for (var i = from; i <= to; i++) {
    result.push(i)
  }
  return result
}

const keyCode = {
  TAB: 9,
  ESC: 27,
  SPACE: 32,
  PAGE_UP: 33,
  PAGE_DOWN: 34,
  END: 35,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
}

let calendarCounter = 0;

@Component({
  tag: "stzh-calendar",
  styleUrl: "stzh-calendar.scss",
  scoped: true
})
export class StzhCalendar implements ComponentInterface {
  /** Date value. Must be in IS0-8601 format: YYYY-MM-DD. */
  @Prop({ mutable: true }) value: string = ""

  /**
   * Minimum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the max property.
   */
  @Prop() min: string = ""

  /**
   * Maximum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the min property.
   */
  @Prop() max: string = ""

  /**
   * Which day is considered first day of the week? `0` for Sunday, `1` for Monday, etc.
   * Default is Monday.
   */
  @Prop() firstDayOfWeek: DaysOfWeek = DaysOfWeek.Monday

  /**  Day names, month names, etc, used for localization. */
  @Prop() localization: StzhCalendarLocalizedText;

  /**
   * Controls which days are disabled and therefore disallowed.
   * For example, this can be used to disallow selection of weekends.
   */
  @Prop() isDateDisabled: StzhCalendarDateDisabledPredicate = () => false

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the previous month button element.
   * Default value will be taken from translations.
   */
  @Prop() prevMonthAnalyticsId: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the next month button element.
   * Default value will be taken from translations.
   */
  @Prop() nextMonthAnalyticsId: string;

  @State() activeFocus = false
  @State() focusedDay = new Date()

  /** Event emitted when a date is selected. */
  @Event() stzhChange: EventEmitter<StzhCalendarChangeEvent>

  @Watch("localization")
  createDateFormatters() {
    if (this.localization) {
      this.dateFormatShort = new Intl.DateTimeFormat(this.localization.$locale, { day: "numeric", month: "long" })
    }
  }

  @Watch("value")
  valueWatcher(newValue: string) {
    if (newValue) {
      this.setFocusedDay(parseISODate(this.value));
    } else {
      this.setFocusedDay(new Date());
    }
  }

  /** Set focused day element by a JS Date object */
  @Method()
  async setFocusedDay(day: Date) {
    this.focusedDay = clamp(day, parseISODate(this.min), parseISODate(this.max))
  }

  /** Set value by a JS Date object */
  @Method()
  async setDate(date: Date) {
    this.value = printISODate(date)
  }

  @Element() element: HTMLStzhCalendarElement

  // private monthSelectId: string
  // private yearSelectId: string
  private dialogLabelId: string

  // private focusedDayNode: HTMLButtonElement

  private initialTouchX: number = null
  private initialTouchY: number = null

  /**
   * To format dates exclusively for the benefit of screen readers.
   *
   * We prefer DateTimeFormat over date.toLocaleDateString, as the former has
   * better performance when formatting large number of dates. See:
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Performance
   */
  private dateFormatShort: Intl.DateTimeFormat

  private enableActiveFocus = () => {
    this.activeFocus = true
  }

  private disableActiveFocus = () => {
    this.activeFocus = false
  }

  private addDays(days: number) {
    this.setFocusedDay(addDays(this.focusedDay, days))
  }

  private addMonths(months: number) {
    this.setMonth(this.focusedDay.getMonth() + months)
  }

  private addYears(years: number) {
    this.setYear(this.focusedDay.getFullYear() + years)
  }

  private startOfWeek() {
    this.setFocusedDay(startOfWeek(this.focusedDay, this.firstDayOfWeek))
  }

  private endOfWeek() {
    this.setFocusedDay(endOfWeek(this.focusedDay, this.firstDayOfWeek))
  }

  private setMonth(month: number) {
    const min = setMonth(startOfMonth(this.focusedDay), month)
    const max = endOfMonth(min)
    const date = setMonth(this.focusedDay, month)

    this.setFocusedDay(clamp(date, min, max))
  }

  private setYear(year: number) {
    const min = setYear(startOfMonth(this.focusedDay), year)
    const max = endOfMonth(min)
    const date = setYear(this.focusedDay, year)

    this.setFocusedDay(clamp(date, min, max))
  }

  private handleTouchStart = (event: TouchEvent) => {
    const touch = event.changedTouches[0]
    this.initialTouchX = touch.pageX
    this.initialTouchY = touch.pageY
  }

  // private handleTouchMove = (event: TouchEvent) => {
  //   event.preventDefault()
  // }

  private handleTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0]
    const distX = touch.pageX - this.initialTouchX // get horizontal dist traveled
    const distY = touch.pageY - this.initialTouchY // get vertical dist traveled
    const threshold = 70

    const isHorizontalSwipe = Math.abs(distX) >= threshold && Math.abs(distY) <= threshold

    if (isHorizontalSwipe) {
      this.addMonths(distX < 0 ? 1 : -1)
    }

    this.initialTouchY = null
    this.initialTouchX = null
  }

  private handleNextMonthClick = (event: MouseEvent) => {
    event.preventDefault()
    this.addMonths(1)
  }

  private handlePreviousMonthClick = (event: MouseEvent) => {
    event.preventDefault()
    this.addMonths(-1)
  }

  private handleKeyboardNavigation = (event: KeyboardEvent) => {
    var handled = true

    switch (event.keyCode) {
      case keyCode.RIGHT:
        this.addDays(1)
        break
      case keyCode.LEFT:
        this.addDays(-1)
        break
      case keyCode.DOWN:
        this.addDays(7)
        break
      case keyCode.UP:
        this.addDays(-7)
        break
      case keyCode.PAGE_UP:
        if (event.shiftKey) {
          this.addYears(-1)
        } else {
          this.addMonths(-1)
        }
        break
      case keyCode.PAGE_DOWN:
        if (event.shiftKey) {
          this.addYears(1)
        } else {
          this.addMonths(1)
        }
        break
      case keyCode.HOME:
        this.startOfWeek()
        break
      case keyCode.END:
        this.endOfWeek()
        break
      default:
        handled = false
    }

    if (handled) {
      event.preventDefault()
      this.enableActiveFocus()
    }
  }

  private handleDaySelect = (_event: MouseEvent, day: Date) => {
    const isInRange = inRange(day, parseISODate(this.min), parseISODate(this.max))
    const isAllowed = !this.isDateDisabled(day)

    if (isInRange && isAllowed) {
      this.setDate(day)

      this.stzhChange.emit({
        component: "stzh-calendar",
        value: this.value,
        valueAsDate: day,
      })
    } else {
      // for consistency we should set the focused day in cases where
      // user has selected a day that has been specifically disallowed
      this.setFocusedDay(day)
    }
  }

  private handleMonthSelect = (event: CustomEvent<StzhDropdownChangeEvent>) => {
    const value = Array.isArray(event.detail.value) ? event.detail.value[0] : event.detail.value;
    this.setMonth(parseInt(value, 10))
  }

  private handleYearSelect = (event: CustomEvent<StzhDropdownChangeEvent>) => {
    const value = Array.isArray(event.detail.value) ? event.detail.value[0] : event.detail.value;
    this.setYear(parseInt(value, 10))
  }

  private processFocusedDayNode = (element: HTMLButtonElement) => {
    // this.focusedDayNode = element

    if (this.activeFocus) {
      setTimeout(() => element.focus(), 0)
    }
  }

  async componentWillLoad() {
    // this.monthSelectId = `stzh-calendar-month-${calendarCounter}`;
    // this.yearSelectId = `stzh-calendar-year-${calendarCounter}`;
    this.dialogLabelId = `stzh-calendar-dialog-${calendarCounter}`;
    calendarCounter++;

    this.valueWatcher(this.value);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, 'calendar');
    }
  }

  connectedCallback() {
    this.createDateFormatters()
  }

  render() {
    const valueAsDate = parseISODate(this.value)
    const selectedYear = (valueAsDate || this.focusedDay).getFullYear()
    const focusedMonth = this.focusedDay.getMonth()
    const focusedYear = this.focusedDay.getFullYear()

    const minDate = parseISODate(this.min)
    const maxDate = parseISODate(this.max)
    const prevMonthDisabled =
      minDate != null && minDate.getMonth() === focusedMonth && minDate.getFullYear() === focusedYear
    const nextMonthDisabled =
      maxDate != null && maxDate.getMonth() === focusedMonth && maxDate.getFullYear() === focusedYear

    const minYear = minDate ? minDate.getFullYear() : selectedYear - 10
    const maxYear = maxDate ? maxDate.getFullYear() : selectedYear + 10

    const monthDropdownOptions = this.localization.$globals.monthNames.map((month, i) => {
      return {
        value: i.toString(),
        text: month,
        disabled: !inRange(
          new Date(focusedYear, i, 1),
          minDate ? startOfMonth(minDate) : null,
          maxDate ? endOfMonth(maxDate) : null
        ),
      }
    });

    const yearDropdownOptions = range(minYear, maxYear).map(year => {
      return {
        value: year.toString(),
        text: year.toString(),
      }
    });

    return (
      <Host>
        <div
          class={{
            "stzh-calendar": true
          }}
          /* onTouchMove={this.handleTouchMove} */
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
        >
          <div class="stzh-calendar__wrapper">
            {/*
            <div class="stzh-calendar__mobile" onFocusin={this.disableActiveFocus}>
              <label class="stzh-calendar__mobile-heading">{this.localization.calendarHeading}</label>
            </div>
            */}
            {/* @ts-ignore */}
            <div class="stzh-calendar__header" onFocusin={this.disableActiveFocus}>
              <div class="stzh-calendar__nav is-dropdowns">
                <h2
                  id={this.dialogLabelId}
                  class="stzh-calendar__vhidden"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {this.localization.$globals.monthNames[focusedMonth]}
                  {this.focusedDay.getFullYear()}
                </h2>

                <stzh-dropdown
                  class="stzh-calendar__dropdown"
                  variant="plain"
                  size="small"
                  inline
                  popover-fullwidth="horizontal"
                  noSearch={true}
                  openOnFocus={false}
                  items={[focusedMonth.toString()]}
                  options={monthDropdownOptions}
                  label={this.localization.monthSelectLabel}
                  labelHidden={true}
                  onStzhChange={this.handleMonthSelect}
                ></stzh-dropdown>

                <stzh-dropdown
                  class="stzh-calendar__dropdown"
                  variant="plain"
                  size="small"
                  inline
                  popover-fullwidth="horizontal"
                  noSearch={true}
                  openOnFocus={false}
                  items={[focusedYear.toString()]}
                  options={yearDropdownOptions}
                  label={this.localization.yearSelectLabel}
                  labelHidden={true}
                  onStzhChange={this.handleYearSelect}
                ></stzh-dropdown>

                {/* alternative for dropdown with popover:
                <stzh-popover>
                  <button class="stzh-calendar__select-label" aria-label={this.localization.monthSelectLabel}>
                    <span>{this.localization.$globals.monthNamesShort[focusedMonth]}</span>
                    <stzh-icon class="stzh-calendar__select-icon" name="angle-down"></stzh-icon>
                  </button>
                  <stzh-menu slot="content">
                    {this.localization.$globals.monthNames.map((month, i) => (
                      <stzh-menu-item
                        // onClick={this.handleMonthSelect(month)}
                        active={i === focusedMonth}
                        disabled={
                          !inRange(
                            new Date(focusedYear, i, 1),
                            minDate ? startOfMonth(minDate) : null,
                            maxDate ? endOfMonth(maxDate) : null
                          )
                        }
                      >
                        {month}
                      </stzh-menu-item>
                    ))}
                  </stzh-menu>
                </stzh-popover>
                */}

                {/* original implmentation with native select:
                <label htmlFor={this.monthSelectId} class="stzh-calendar__vhidden">
                  {this.localization.monthSelectLabel}
                </label>

                <div class="stzh-calendar__select-wrapper">
                  <select
                    id={this.monthSelectId}
                    class="stzh-calendar__select stzh-calendar__select--month"
                    onChange={this.handleMonthSelect}
                  >
                    {this.localization.$globals.monthNames.map((month, i) => (
                      <option
                        key={month}
                        value={i}
                        selected={i === focusedMonth}
                        disabled={
                          !inRange(
                            new Date(focusedYear, i, 1),
                            minDate ? startOfMonth(minDate) : null,
                            maxDate ? endOfMonth(maxDate) : null
                          )
                        }
                      >
                        {month}
                      </option>
                    ))}
                  </select>
                  <div class="stzh-calendar__select-label" aria-hidden="true">
                    <span>{this.localization.$globals.monthNamesShort[focusedMonth]}</span>
                    <stzh-icon class="stzh-calendar__select-icon" name="angle-down"></stzh-icon>
                  </div>
                </div>

                <label htmlFor={this.yearSelectId} class="stzh-calendar__vhidden">
                  {this.localization.yearSelectLabel}
                </label>

                <div class="stzh-calendar__select-wrapper">
                  <select
                    id={this.yearSelectId}
                    class="stzh-calendar__select stzh-calendar__select--year"
                    onChange={this.handleYearSelect}
                  >
                    {range(minYear, maxYear).map(year => (
                      <option key={year} selected={year === focusedYear}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <div class="stzh-calendar__select-label" aria-hidden="true">
                    <span>{this.focusedDay.getFullYear()}</span>
                    <stzh-icon class="stzh-calendar__select-icon" name="angle-down"></stzh-icon>
                  </div>
                </div>
                */}

              </div>

              <div class="stzh-calendar__nav">
                <stzh-button
                  class="stzh-calendar__prev"
                  variant="tertiary"
                  size="small"
                  icon="arrow-left"
                  iconOnly={true}
                  onClick={this.handlePreviousMonthClick}
                  disabled={prevMonthDisabled}
                  type="button"
                  a11yLabel={this.localization.prevMonthLabel}
                  analyticsId={this.prevMonthAnalyticsId || this.localization.prevMonthLabel}
                ></stzh-button>
                <stzh-button
                  class="stzh-calendar__next"
                  variant="tertiary"
                  size="small"
                  icon="arrow-right"
                  iconOnly={true}
                  onClick={this.handleNextMonthClick}
                  disabled={nextMonthDisabled}
                  type="button"
                  a11yLabel={this.localization.nextMonthLabel}
                  analyticsId={this.nextMonthAnalyticsId || this.localization.nextMonthLabel}
                ></stzh-button>
              </div>
            </div>

            <StzhCalendarMonth
              dateFormatter={this.dateFormatShort}
              selectedDate={valueAsDate}
              focusedDate={this.focusedDay}
              onDateSelect={this.handleDaySelect}
              onKeyboardNavigation={this.handleKeyboardNavigation}
              labelledById={this.dialogLabelId}
              localization={this.localization}
              firstDayOfWeek={this.firstDayOfWeek}
              focusedDayRef={this.processFocusedDayNode}
              min={minDate}
              max={maxDate}
              isDateDisabled={this.isDateDisabled}
            />
          </div>
        </div>
      </Host>
    )
  }
}
